/**
 * A collection's sounds as a Spotify-style track table: "#" (a play / pause button on row hover or focus; the playing
 * row's number turns into a purple equalizer), Title (40px cover, title with the brand logo, "Like “X”" / source),
 * Genre, Date added, duration and the sound's ⋯ menu. Rows are 56px with hairline dividers; Genre and Date added hide
 * on phones. Playing a row queues the whole table from that row (`queueKey`), so the player moves on by itself.
 */
import { Clock3, Pause, Play } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import type { StudioTrack } from '@/api/audioStudioApi';
import { usePlayer } from './AudioPlayer';
import { Equalizer } from './CollectionArt';
import { GenreTile } from './CoverArt';
import { RenameInline, TrackBrandMark, TrackMenu } from './SoundRows';
import { capitalize, formatAgo, formatClock, studioPlayable, trackOrigin } from './format';
import { RIGHTS } from './rights';
import type { TrackActions } from './useTrackActions';

const gridCls = 'grid grid-cols-[28px_minmax(0,1fr)_44px_32px] items-center gap-3 px-2 sm:grid-cols-[28px_minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_52px_32px]';

export function TrackTable({ tracks, actions, queueKey, label, now, showOwner = false }: {
  tracks: StudioTrack[];
  actions: TrackActions;
  queueKey: string;
  /** Accessible name of the list, e.g. "Summer drop sounds". */
  label: string;
  now?: number;
  /** Team lists: name the maker when it isn't you. */
  showOwner?: boolean;
}) {
  const { current, isPlaying, pauseResume, playQueue } = usePlayer();
  const items = tracks.map(studioPlayable);

  return (
    <div>
      <div aria-hidden className={cn(gridCls, 'h-9 border-b border-[#eaecf0] text-[11.5px] uppercase tracking-wide text-[#98a2b3]')} style={{ fontWeight: 600 }}>
        <span className="text-center">#</span>
        <span>Title</span>
        <span className="hidden sm:block">Genre</span>
        <span className="hidden sm:block">Date added</span>
        <span className="flex justify-end"><Clock3 className="h-3.5 w-3.5" /></span>
        <span />
      </div>
      <ol aria-label={label} className="mt-1">
        {tracks.map((t, i) => {
          const item = items[i];
          const active = current?.id === item.id;
          const playing = active && isPlaying;
          const title = t.title || 'Untitled';
          const owner = showOwner && !t.is_owner ? t.owner_name || 'Team member' : null;
          const sub = [owner, trackOrigin(t)].filter(Boolean).join(' · ');
          const genre = capitalize(t.genre);
          const play = () => (active ? pauseResume() : playQueue(items, i, { key: queueKey }));
          return (
            <li
              key={t.track_id}
              data-track-id={t.track_id}
              aria-current={active ? 'true' : undefined}
              onDoubleClick={() => { if (!active) play(); }}
              className={cn(gridCls, 'group/tr h-14 rounded-[8px] transition-colors hover:bg-[#f9fafb] focus-within:bg-[#f9fafb]', active && 'bg-[#fcfaff]')}
            >
              <button
                type="button"
                onClick={play}
                disabled={!item.audioUrl}
                aria-label={playing ? `Pause ${title}` : `Play ${title}`}
                className="flex h-8 w-7 items-center justify-center rounded-md text-[#667085] outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className={cn('text-[13px] tabular-nums group-hover/tr:hidden group-focus-within/tr:hidden', active && 'text-[#be43ff]')}>
                  {playing ? <Equalizer /> : i + 1}
                </span>
                <span className="hidden text-[#101828] group-hover/tr:inline-flex group-focus-within/tr:inline-flex">
                  {playing ? <Pause className="h-4 w-4" fill="currentColor" aria-hidden /> : <Play className="h-4 w-4" fill="currentColor" aria-hidden />}
                </span>
              </button>

              <div className="flex min-w-0 items-center gap-3">
                <span className="h-10 w-10 shrink-0 overflow-hidden rounded-[6px] bg-[#f2f4f7]">
                  {t.cover_url
                    ? <img src={t.cover_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                    : <GenreTile genre={genre || null} title={title} instrumental={t.music_type === 'Instrumental'} size="thumb" />}
                </span>
                <div className="min-w-0 flex-1">
                  {actions.renamingId === t.track_id ? (
                    <RenameInline track={t} onDone={actions.stopRename} />
                  ) : (
                    <>
                      <div className="flex min-w-0 items-center gap-1.5">
                        <TrackBrandMark track={t} />
                        <span className={cn('truncate text-[14px] leading-5', active ? 'text-[#be43ff]' : 'text-[#101828]')} style={{ fontWeight: 600 }} title={`${title}\n${RIGHTS.original.tooltip}`}>{title}</span>
                      </div>
                      <p className="truncate text-[12.5px] leading-[18px] text-[#667085]" title={sub}>{sub}</p>
                    </>
                  )}
                </div>
              </div>

              <span className="hidden truncate text-[13px] text-[#667085] sm:block">{genre || '—'}</span>
              <span className="hidden truncate text-[13px] text-[#667085] sm:block">{formatAgo(t.created_at, now)}</span>
              <span className="text-right text-[13px] tabular-nums text-[#667085]" aria-label="Duration">{t.duration_s ? formatClock(t.duration_s) : '—'}</span>
              <TrackMenu
                track={t}
                actions={actions}
                triggerClassName="flex h-8 w-8 items-center justify-center rounded-md text-[#667085] opacity-0 transition-opacity hover:bg-[#f2f4f7] focus-visible:opacity-100 group-hover/tr:opacity-100 group-focus-within/tr:opacity-100 data-[state=open]:bg-[#f2f4f7] data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100"
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
